import type { Project } from "../api/models";

interface LinkedClientProjectSeed {
    id: string;
    name: string;
    organizationId: string;
    businessAreaId: string;
    businessAreaName: string;
    unitId: string;
    unitName: string;
    createdAt: string;
    updatedAt: string;
}

interface StructureLabelScope {
    id: string;
    name: string;
    parentId: string | null;
    scopeType: string;
}

export const toOptimisticLinkedProject = (client: LinkedClientProjectSeed): Project => ({
    id: client.id,
    name: client.name,
    created_at: client.createdAt,
    updated_at: client.updatedAt,
    organization_id: client.organizationId,
    business_area_id: client.businessAreaId,
    business_area_name: client.businessAreaName,
    unit_id: client.unitId,
    unit_name: client.unitName,
    location_id: client.unitId,
    location_name: `${client.businessAreaName} · ${client.unitName}`,
    organization_link_status: "linked"
});

export const upsertProjectList = (projects: Project[], project: Project): Project[] => {
    const existing = projects.find(item => item.id === project.id);
    return [{ ...existing, ...project }, ...projects.filter(item => item.id !== project.id)];
};

export const syncLinkedUnitProjectName = (project: Project, organizationId: string, unitId: string, unitName: string): Project => {
    if (project.organization_id !== organizationId || project.unit_id !== unitId) return project;
    const businessAreaName = project.business_area_name || "Business Area";
    return { ...project, unit_name: unitName, location_name: `${businessAreaName} · ${unitName}` };
};

export const syncLinkedBusinessAreaProjectName = (project: Project, organizationId: string, businessAreaId: string, businessAreaName: string): Project => {
    if (project.organization_id !== organizationId || project.business_area_id !== businessAreaId) return project;
    const unitName = project.unit_name || project.location_name || "Unit";
    return { ...project, business_area_name: businessAreaName, location_name: `${businessAreaName} · ${unitName}` };
};

export const syncLinkedProjectStructureLabels = (
    project: Project,
    organizationId: string,
    scopes: readonly StructureLabelScope[]
): Project => {
    if (project.organization_id !== organizationId || !project.unit_id) return project;
    const scopesById = new Map(scopes.map(scope => [scope.id, scope]));
    const unit = scopesById.get(project.unit_id);
    if (!unit || unit.scopeType !== "Unit") return project;
    const businessArea = unit.parentId ? scopesById.get(unit.parentId) : undefined;
    const businessAreaName = businessArea?.scopeType === "Business Area" ? businessArea.name : project.business_area_name || "Business Area";
    const locationName = `${businessAreaName} · ${unit.name}`;
    if (project.unit_name === unit.name && project.business_area_name === businessAreaName && project.location_name === locationName) return project;
    return {
        ...project,
        business_area_id: businessArea?.scopeType === "Business Area" ? businessArea.id : project.business_area_id,
        business_area_name: businessAreaName,
        unit_name: unit.name,
        location_id: unit.id,
        location_name: locationName
    };
};
